/* Guided tour of the whole client.
 *
 * ── Why this is a tour and not a slideshow ──────────────────────────────────
 *
 * A demo that consists of "let me click around and find something interesting" wastes the
 * audience's attention. But one that only *narrates* teaches nobody how to drive the thing — and
 * "learnable in a day" is Command PE's real moat (`COMMAND-PE-TEARDOWN.md` §14), so it is worth
 * attacking directly rather than hoping a feature list wins on its own.
 *
 * Each beat therefore does three things at once:
 *
 *   1. **Performs the action.** Plots the course, starts the clock, toggles the layer. The audience
 *      watches the real feature working on real kernel state — the aircraft actually flies the path
 *      the tour draws, because the tour issues the same order an operator would.
 *   2. **Names the control**, in a "TRY IT" line. This is what turns a demo into onboarding.
 *   3. **Says why it matters** — one sentence of tactical or architectural point.
 *
 * Doubles as the onboarding walkthrough (`UI-UX-DESIGN.md` §12), which is why every beat sets the
 * state it needs rather than assuming the previous beat ran. Jumping straight to beat 12 works.
 *
 * ── Autoplay ───────────────────────────────────────────────────────────────
 *
 * The commonest demo failure is a presenter narrating while fumbling for the next click. Each beat
 * declares its own dwell: one that starts the clock and waits for aircraft to move needs longer
 * than one that toggles a layer.
 */
import { useEffect } from 'react'
import { ChevronLeft, ChevronRight, Pause, Play, Repeat, X } from 'lucide-react'
import type { Map as MlMap } from 'maplibre-gl'
import { useDeck, type DetailTab, type LayerId, type Tool } from '../state/store'
import type { BasemapId } from '../lib/basemaps'

interface Beat {
  title: string
  /** The point. One sentence. */
  say: string
  /** The control that does this, in the operator's terms. Omitted only for pure narration. */
  tryIt?: string
  /** Seconds to dwell in autoplay. Beats that wait for the world to change need longer. */
  dwell?: number

  camera?: { center: [number, number]; zoom: number }
  /** `null` clears the selection; `undefined` leaves it alone. Different intentions. */
  select?: { kind: 'entity' | 'contact'; id: string } | null
  tab?: DetailTab
  view?: '2d' | '3d'
  basemap?: BasemapId
  tool?: Tool
  layersOn?: LayerId[]
  layersOff?: LayerId[]
  panel?: 'none' | 'layers' | 'missions' | 'orbat' | 'losses' | 'analysis'
  run?: boolean
  compression?: number
  /** Plot a course for real — sent to the kernel as an order, exactly like an operator click. */
  plotCourse?: { entityId: string; waypoints: [number, number][] }
  clearCourses?: boolean
  measure?: [[number, number], [number, number]]
}

const VIPER_11 = '018f2c9e-0001'
const OLFERT = '018f2c9e-0004'

const BEATS: Beat[] = [
  {
    title: 'The map is the application',
    say: 'Baltic Approaches, March 1991. Real OSM geography, MIL-STD-2525E symbology, and every number on screen computed by the ORBIS kernel rather than scripted.',
    tryIt: 'Nothing to press yet. Note the LIVE CORE badge in the status bar — it reads FIXTURE when the numbers are not real, so you never have to wonder.',
    camera: { center: [14.2, 55.5], zoom: 5.9 },
    view: '2d',
    basemap: 'tactical',
    panel: 'none',
    tool: 'select',
    select: null,
    clearCourses: true,
    run: false,
    compression: 1,
    layersOn: ['units', 'contacts', 'tracks', 'threatEnvelopes'],
    layersOff: ['sensorRanges', 'weaponEnvelopes'],
    dwell: 10,
  },
  {
    title: 'Start and stop the clock',
    say: 'The simulation is a deterministic kernel. Pausing simply stops calling it, so a run paused for an hour produces a byte-identical result to one that was not — pacing lives outside the physics.',
    tryIt: 'SPACE, or the play/pause button at top right. The TICK counter starts moving.',
    run: true,
    compression: 1,
    camera: { center: [14.0, 55.5], zoom: 6.2 },
    dwell: 8,
  },
  {
    title: 'Compress time',
    say: '×1 to ×300. Compression changes how often the kernel is stepped, never what it computes, so results are identical at any speed. Watch the aircraft move and the fuel figures fall.',
    tryIt: 'The ×1 ×5 ×15 ×60 ×300 buttons in the command bar.',
    compression: 60,
    run: true,
    dwell: 11,
  },
  {
    title: 'Draw a course — and the aircraft flies it',
    say: 'A plotted course is an order the kernel ingests at a tick boundary. VIPER 11 turns onto the first leg immediately. The panel prices every leg in range, time and fuel-on-arrival before you commit to it.',
    tryIt: 'Press C for the course tool, click a unit, then click the map to add waypoints. ↶ undoes the last one, the bin clears the plan.',
    tool: 'course',
    select: { kind: 'entity', id: VIPER_11 },
    plotCourse: {
      entityId: VIPER_11,
      waypoints: [
        [13.95, 55.72],
        [14.85, 55.95],
        [15.35, 55.45],
      ],
    },
    camera: { center: [14.5, 55.7], zoom: 6.6 },
    compression: 15,
    run: true,
    dwell: 15,
  },
  {
    title: 'A plan belongs to the unit, not to the tool',
    say: 'Plot a second course and the first survives. Each unit keeps its own plan; the one being worked on draws at full strength and the others dimmed — because the reason to plot two is to compare them.',
    tryIt: 'Select another unit and keep clicking. The panel lists the other plans under "Also plotted", and you can jump straight back to one.',
    plotCourse: {
      entityId: OLFERT,
      waypoints: [
        [13.3, 55.05],
        [13.95, 54.85],
      ],
    },
    select: { kind: 'entity', id: OLFERT },
    camera: { center: [13.8, 55.3], zoom: 6.4 },
    dwell: 13,
  },
  {
    title: 'Layers — what we can see',
    say: 'Sensor coverage. The AEW orbit dominates at 200 nm. VIPER 12 draws nothing because it is EMCON silent, and a radar that is not radiating has no coverage. Not a display quirk: the kernel refuses its detections for the same reason, and its trace says so.',
    tryIt: 'L opens the layers panel, or use the layers button in the command bar. Toggle "Sensor ranges".',
    panel: 'layers',
    layersOn: ['sensorRanges'],
    camera: { center: [13.8, 55.6], zoom: 5.7 },
    run: false,
    dwell: 13,
  },
  {
    title: 'Layers — what can shoot us',
    say: 'The assessed threat envelope around Kaliningrad is why the CAP station sits where it does. Assessed, not truth: it is drawn from what BLUE believes about that site, and it is labelled that way.',
    tryIt: 'Same panel — threat envelopes, weapon envelopes, missions, zones, graticule, terrain. Each toggles independently.',
    layersOn: ['threatEnvelopes', 'weaponEnvelopes'],
    camera: { center: [16.4, 55.1], zoom: 5.3 },
    dwell: 11,
  },
  {
    title: 'Basemaps, and the globe',
    say: 'Four basemaps. 2D and 3D are the same renderer over the same style — MapLibre globe projection, not a second scene graph — so there is nothing that can diverge between the two views.',
    tryIt: 'The basemap menu in the command bar, and the 2D / 3D buttons. Keys 2 and 3.',
    panel: 'none',
    view: '3d',
    basemap: 'satellite',
    layersOff: ['weaponEnvelopes'],
    dwell: 11,
  },
  {
    title: 'Measure anything',
    say: 'Geodesic range and bearing on the WGS84 ellipsoid — Vincenty, matched to the published reference vector to the millimetre. A flat-earth approximation would be out by miles at this range.',
    tryIt: 'Press M, then click twice on the map. Esc drops the measurement without losing your selection.',
    view: '2d',
    basemap: 'tactical',
    tool: 'measure',
    measure: [
      [13.18, 55.42],
      [15.35, 55.45],
    ],
    camera: { center: [14.3, 55.5], zoom: 6.3 },
    dwell: 11,
  },
  {
    title: 'Contacts are estimates, never truth',
    say: 'These tracks are what BLUE has actually detected. Each carries a classification level, a confidence and an uncertainty in nautical miles — and the client is never sent more than the classification level has earned.',
    tryIt: 'Press V for the select tool and click a track. B opens the contact list, sorted by descending confidence.',
    tool: 'select',
    panel: 'orbat',
    camera: { center: [14.6, 55.6], zoom: 6.4 },
    run: true,
    compression: 15,
    dwell: 14,
  },
  {
    title: 'Radar tells you where. ESM tells you what.',
    say: 'An emitting target is identified almost at once — an emitter is nearly a fingerprint — but located poorly. A silent one is located well and barely classified. That asymmetry is what makes EMCON a real decision rather than a checkbox.',
    tryIt: 'Compare two tracks in the list: confidence against uncertainty. The high-confidence ones were found by their own transmissions.',
    panel: 'orbat',
    run: false,
    dwell: 14,
  },
  {
    title: 'Why the weapon did not fire',
    say: 'The feature Command PE does not have. The whole check chain in order, the failing gate named, and the actions that would resolve it. Their manual has a section called "My weapon won\'t fire!!!" — this is the answer to it.',
    tryIt: 'Select a unit, then the Trace tab in the detail panel on the right.',
    panel: 'none',
    select: { kind: 'entity', id: VIPER_11 },
    tab: 'trace',
    camera: { center: [13.8, 55.5], zoom: 6.8 },
    dwell: 16,
  },
  {
    title: 'Doctrine is data, and its inheritance is visible',
    say: 'WEAPONS TIGHT is set at side level and inherited down. The effective value is stated first and its source is named — the part operators normally have to guess at.',
    tryIt: 'The Doctrine tab.',
    tab: 'doctrine',
    dwell: 10,
  },
  {
    title: 'Damage is per component, never a hit-point pool',
    say: 'The corvette lost its Mk 91 director. Launcher and magazine are intact, but a semi-active missile cannot be guided — so the chain breaks at illumination and the trace says exactly that. A hit-point pool cannot express it.',
    tryIt: 'Select the corvette, then the Damage tab.',
    select: { kind: 'entity', id: OLFERT },
    tab: 'damage',
    camera: { center: [12.9, 55.05], zoom: 7.2 },
    dwell: 13,
  },
  {
    title: 'Missions, order of battle, losses',
    say: 'Mission assignment with a live feasibility verdict that always charges the return leg — a unit that cannot get home is not on station, it is lost. Plus a full ORBAT and a running attrition view with observed Pk, labelled "observed" because at single-digit shot counts that is what it is.',
    tryIt: 'O for missions, B for order of battle, X for losses, ` for the log. Cmd-K opens the command palette for everything at once.',
    panel: 'missions',
    select: null,
    camera: { center: [14.0, 55.5], zoom: 5.9 },
    dwell: 13,
  },
  {
    title: 'Reproducible by construction',
    say: 'The status bar pins the dataset digest, the bundle digest and the RNG seed. Re-run this scenario with that seed on any machine and the event stream is byte-identical — verified across macOS/arm64 and Linux/x86-64. That is what makes the analysis worth anything, and it is the thing Command PE cannot offer at all.',
    tryIt: 'Read the bottom right: DATASET, BUNDLE, SEED.',
    panel: 'none',
    view: '2d',
    basemap: 'tactical',
    camera: { center: [14.2, 55.5], zoom: 5.9 },
    run: false,
    compression: 1,
    dwell: 15,
  },
  {
    // The close, and deliberately last: everything before this is one run. A demo that ends on a
    // moving map has shown a picture; this is the beat that turns it into an answer, and it is
    // the question an analyst is actually paid to answer.
    title: 'One run is an anecdote. Eight are an answer.',
    say: 'The same scenario, run eight times over a derived seed matrix. RED loses 4.75 platforms to BLUE\u2019s 0.88 \u2014 an exchange ratio of 3.9, with a 95% interval, from 4.8 rounds per kill. Every row names its interval type, because a rate and a mean are not the same claim. And the batch re-ran a seed and compared digests, so it can say it was reproducible rather than hoping.',
    tryIt: 'Y opens it. Read the bottom row: time_to_first_shot_s is identical in every run, so it is reported as carrying NO INFORMATION rather than as the most confident number on the page \u2014 RED starts inside its own missile envelope. That refusal is the difference between an analysis view and a chart.',
    panel: 'analysis',
    select: null,
    run: false,
    compression: 1,
    dwell: 20,
  },
]

export function DemoScript({ map }: { map: MlMap | null }) {
  const step = useDeck((s) => s.demoStep)
  const setStep = useDeck((s) => s.setDemoStep)
  const autoplay = useDeck((s) => s.demoAutoplay)
  const setAutoplay = useDeck((s) => s.setDemoAutoplay)

  const select = useDeck((s) => s.select)
  const setDetailTab = useDeck((s) => s.setDetailTab)
  const setViewMode = useDeck((s) => s.setViewMode)
  const setBasemap = useDeck((s) => s.setBasemap)
  const setTool = useDeck((s) => s.setTool)
  const toggleLayer = useDeck((s) => s.toggleLayer)
  const setRunning = useDeck((s) => s.setRunning)
  const setCompression = useDeck((s) => s.setCompression)
  const courseAdd = useDeck((s) => s.courseAdd)
  const courseClear = useDeck((s) => s.courseClear)
  const measurePick = useDeck((s) => s.measurePick)
  const measureClear = useDeck((s) => s.measureClear)
  const setPanel = useDeck((s) => s.setPanel)

  const apply = (i: number) => {
    const b = BEATS[i]
    if (!b) return

    // View and basemap first, because they trigger a style reload; the camera goes last and is
    // delayed past it, or the ease fights the reload and lands somewhere unintended.
    if (b.view) setViewMode(b.view)
    if (b.basemap) setBasemap(b.basemap)
    if (b.panel) setPanel(b.panel)
    if (b.tool) setTool(b.tool)
    if (b.select !== undefined) select(b.select)
    if (b.tab) setDetailTab(b.tab)

    // Layers are read from the store at apply time, not captured in the closure: a beat jumped to
    // out of order would otherwise toggle against a stale snapshot and set the opposite of what it
    // meant to.
    const current = useDeck.getState().layers
    for (const l of b.layersOn ?? []) if (!current[l]) toggleLayer(l)
    for (const l of b.layersOff ?? []) if (current[l]) toggleLayer(l)

    if (b.clearCourses) courseClear()
    if (b.plotCourse) {
      // Clear first, so replaying a beat cannot stack duplicate legs on the same unit.
      courseClear(b.plotCourse.entityId)
      for (const [lon, lat] of b.plotCourse.waypoints) {
        courseAdd(b.plotCourse.entityId, { lon, lat })
      }
    }

    // Always cleared, then set if this beat wants one — otherwise a measurement from an earlier
    // beat hangs on the map for the rest of the tour.
    measureClear()
    if (b.measure) for (const [lon, lat] of b.measure) measurePick({ lon, lat })

    if (b.compression !== undefined) setCompression(b.compression)
    if (b.run !== undefined) setRunning(b.run)

    if (b.camera && map) {
      const delay = b.view ? 450 : 0
      window.setTimeout(
        () => map.flyTo({ center: b.camera!.center, zoom: b.camera!.zoom, duration: 1600 }),
        delay,
      )
    }
    setStep(i)
  }

  // Autoplay. Keyed on the step so each beat's own dwell applies, and cleared on unmount so
  // exiting cannot leave a timer that advances a closed tour.
  useEffect(() => {
    if (step === null || !autoplay) return
    const ms = (BEATS[step]?.dwell ?? 10) * 1000
    const id = window.setTimeout(() => {
      if (step + 1 < BEATS.length) apply(step + 1)
      else setAutoplay(false)
    }, ms)
    return () => window.clearTimeout(id)
    // `apply` is a new function every render; depending on it would restart the timer constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, autoplay])

  const exit = () => {
    setAutoplay(false)
    setStep(null)
    setRunning(false)
    measureClear()
  }

  if (step === null) {
    return (
      <button
        className="ctl absolute bottom-3 left-3 z-20"
        style={{
          background: 'var(--color-surface-1)',
          border: '1px solid var(--color-line-2)',
          minHeight: 28,
        }}
        onClick={() => apply(0)}
        aria-label="Run the guided tour"
        title={`Guided tour — ${BEATS.length} beats covering every control`}
      >
        <Play size={12} />
        <span style={{ fontSize: 11.5 }}>Guided tour</span>
      </button>
    )
  }

  const b = BEATS[step]

  return (
    <div className="panel absolute bottom-3 left-3 z-20 w-[28rem] overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <div className="flex items-center gap-2">
          <span className="label" style={{ fontSize: 9 }}>
            Guided tour
          </span>
          <span className="num" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
            {step + 1} / {BEATS.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            className="ctl"
            style={{
              width: 20,
              height: 20,
              padding: 0,
              color: autoplay ? 'var(--color-accent)' : undefined,
            }}
            onClick={() => setAutoplay(!autoplay)}
            aria-label={autoplay ? 'Pause autoplay' : 'Advance the tour automatically'}
            title={autoplay ? 'Pause autoplay' : 'Advance the tour automatically'}
          >
            {autoplay ? <Pause size={11} /> : <Repeat size={11} />}
          </button>
          <button
            className="ctl"
            style={{ width: 20, height: 20, padding: 0 }}
            onClick={exit}
            aria-label="Exit the tour"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      <div className="px-3 py-2">
        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--color-text-1)' }}>
          {b.title}
        </div>
        <p
          className="mt-1"
          style={{ fontSize: 11.5, color: 'var(--color-text-2)', lineHeight: 1.5 }}
        >
          {b.say}
        </p>
        {b.tryIt && (
          <p
            className="mt-2 px-2 py-1.5"
            style={{
              fontSize: 11,
              color: 'var(--color-text-2)',
              lineHeight: 1.45,
              background: 'var(--color-surface-2)',
              borderLeft: '2px solid var(--color-accent)',
            }}
          >
            <span className="label" style={{ fontSize: 8.5, color: 'var(--color-accent)' }}>
              TRY IT{' '}
            </span>
            {b.tryIt}
          </p>
        )}
      </div>

      <div
        className="flex items-center gap-1 px-3 py-1.5"
        style={{ borderTop: '1px solid var(--color-line-2)' }}
      >
        <button
          className="ctl"
          disabled={step === 0}
          onClick={() => apply(step - 1)}
          aria-label="Previous beat"
        >
          <ChevronLeft size={13} />
        </button>
        <div className="flex flex-1 items-center gap-1">
          {BEATS.map((beat, i) => (
            <button
              key={i}
              className="h-1 flex-1 rounded-full transition-colors"
              style={{ background: i <= step ? 'var(--color-accent)' : 'var(--color-surface-3)' }}
              onClick={() => apply(i)}
              aria-label={`Beat ${i + 1}: ${beat.title}`}
              title={beat.title}
            />
          ))}
        </div>
        <button
          className="ctl"
          disabled={step === BEATS.length - 1}
          onClick={() => apply(step + 1)}
          aria-label="Next beat"
        >
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  )
}
